import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Play, CheckCircle, Clock, BarChart3, Trophy,
  BookOpen, ChevronRight
} from 'lucide-react';

const chapters = [
  { id: 1, title: 'Introduction', time: '00:00', duration: '2:30', completed: true },
  { id: 2, title: 'Safety checklist', time: '02:30', duration: '2:45', completed: true },
  { id: 3, title: 'Rigging procedure', time: '05:15', duration: '3:30', completed: false, current: true },
  { id: 4, title: 'Connection sequence', time: '08:45', duration: '2:35', completed: false },
  { id: 5, title: 'Final checks', time: '11:20', duration: '1:25', completed: false },
];

const relatedCourses = [
  { id: 'thread-wash', title: 'Thread Washing Systems', duration: '30 min' },
  { id: 'torque-monitoring', title: 'Torque Monitoring Computers', duration: '25 min' },
];

export default function CourseDetail() {
  const navigate = useNavigate();
  const { courseId } = useParams();

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground">Power Tongs Setup</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
        {/* Video Preview */}
        <div
          className="relative aspect-video bg-fieldiq-card rounded-xl flex items-center justify-center cursor-pointer group"
          onClick={() => navigate(`/training/video/${courseId || 'power-tongs'}`)}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-fieldiq-dark/80 to-transparent rounded-xl" />
          <div className="w-14 h-14 rounded-full bg-fieldiq-accent/90 flex items-center justify-center z-10 group-hover:scale-110 transition-transform">
            <Play className="w-6 h-6 text-fieldiq-dark ml-0.5" />
          </div>
          <div className="absolute bottom-3 left-3 z-10">
            <p className="text-xs font-semibold text-white">Power Tongs Setup - Step by Step</p>
            <p className="text-[10px] text-fieldiq-muted">12:45 · 67% complete</p>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-white">Your Progress</span>
            <span className="text-xs font-bold text-fieldiq-accent">67%</span>
          </div>
          <div className="w-full bg-fieldiq-dark rounded-full h-2">
            <div className="bg-fieldiq-accent-signal h-2 rounded-full" style={{ width: '67%' }} />
          </div>
          <div className="flex items-center gap-4 mt-3 text-[10px] text-fieldiq-muted">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> 8:23 remaining</span>
            <span className="flex items-center gap-1"><BarChart3 className="w-3 h-3" /> Intermediate</span>
          </div>
        </div>

        {/* Chapters */}
        <section>
          <h2 className="text-sm font-bold text-white mb-2">Chapters</h2>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl divide-y divide-fieldiq-border">
            {chapters.map((ch) => (
              <button
                key={ch.id}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left ${ch.current ? 'bg-fieldiq-accent/10' : ''}`}
                onClick={() => navigate(`/training/video/${courseId || 'power-tongs'}`)}
              >
                {ch.completed ? (
                  <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                ) : ch.current ? (
                  <Play className="w-4 h-4 text-fieldiq-accent shrink-0" />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-fieldiq-muted/30 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${ch.current ? 'text-fieldiq-accent' : 'text-white'}`}>{ch.title}</p>
                  <p className="text-[10px] text-fieldiq-muted">{ch.time} · {ch.duration}</p>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* What You'll Learn */}
        <section>
          <h2 className="text-sm font-bold text-white mb-2">What You'll Learn</h2>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-3 space-y-2">
            {[
              'Safe setup and rigging procedures for power tongs',
              'Proper torque settings for different casing sizes',
              'Daily maintenance and pre-job inspection checks',
              'Emergency shutdown procedures',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-green-400 mt-0.5 shrink-0" />
                <p className="text-xs text-fieldiq-text">{item}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Certification */}
        <div className="bg-fieldiq-accent/10 border border-fieldiq-accent/30 rounded-xl p-3 flex items-center gap-3">
          <Trophy className="w-8 h-8 text-fieldiq-accent shrink-0" />
          <div>
            <p className="text-xs font-semibold text-white">Earn Certification</p>
            <p className="text-[10px] text-fieldiq-text">Complete all chapters and pass the quiz to earn your Power Tongs Operator badge.</p>
          </div>
        </div>

        {/* Related */}
        <section>
          <h2 className="text-sm font-bold text-white mb-2">Related Courses</h2>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl divide-y divide-fieldiq-border">
            {relatedCourses.map((c) => (
              <button key={c.id} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-fieldiq-dark/50 transition-colors" onClick={() => navigate(`/training/course/${c.id}`)}>
                <BookOpen className="w-4 h-4 text-fieldiq-accent shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-white">{c.title}</p>
                  <p className="text-[10px] text-fieldiq-muted">{c.duration}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-fieldiq-muted" />
              </button>
            ))}
          </div>
        </section>

        {/* Actions */}
        <button
          className="w-full flex items-center justify-center gap-1 bg-fieldiq-accent text-fieldiq-dark text-sm font-semibold py-3 rounded-xl hover:bg-amber-400 transition-colors"
          onClick={() => navigate(`/training/video/${courseId || 'power-tongs'}`)}
        >
          <Play className="w-4 h-4" /> Continue Training
        </button>
      </div>
    </div>
  );
}
